import { useCallback } from "react";
import { FieldValues, useController } from "react-hook-form";

import { CountrySelect, ICountrySelectProps } from "../input/CountrySelect";
import { FieldPathOfType, IControllerProps } from "./formTypes";

type IControlledCountrySelectProps = Omit<ICountrySelectProps, "value">;

interface IProps<TFieldValues extends FieldValues, TName extends FieldPathOfType<TFieldValues, string>>
    extends IControlledCountrySelectProps {
    name: TName;
    controllerProps?: IControllerProps<TFieldValues, TName, string>;
}

export function ControllerCountrySelect<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPathOfType<TFieldValues, string> = FieldPathOfType<TFieldValues, string>,
>({
    name,
    controllerProps,
    onChange,
    onBlur,
    ...countrySelectProps
}: IProps<TFieldValues, TName>): React.ReactElement | null {
    const {
        field: { value, onChange: onFieldChange, onBlur: onFieldBlur, ref },
    } = useController({ ...controllerProps, name });

    const handleChange = useCallback(
        (option: string | undefined): void => {
            onFieldChange(option ?? null);
            onChange?.(option as never);
        },
        [onFieldChange, onChange],
    );
    const handleBlur = useCallback((): void => {
        onFieldBlur();
        onBlur?.();
    }, [onFieldBlur, onBlur]);

    return (
        <CountrySelect {...countrySelectProps} ref={ref} value={value} onChange={handleChange} onBlur={handleBlur} />
    );
}
